/* eslint-disable no-console */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface VersionCheckResult {
	hasUpdate: boolean;
	currentVersion: string;
	serverVersion: string;
	reloadToUpdate: () => void;
	dismissUpdate: () => void;
}

// Use the build-time version from Next.js config as the source of truth for current version
const CLIENT_VERSION = process.env.APP_VERSION || "1";
const IS_DEV = process.env.NODE_ENV === "development";

export function useVersionCheck(checkInterval: number = 60000): VersionCheckResult {
	const [serverVersion, setServerVersion] = useState<string>("");
	const [hasUpdate, setHasUpdate] = useState(false);
	const hasCheckedRef = useRef(false);

	// Reload to get the new version
	const reloadToUpdate = () => {
		if (typeof window !== "undefined") {
			try {
				// Clear localStorage flags so user sees notification for next update
				localStorage.removeItem("update-dismissed");
				sessionStorage.removeItem("update-notification-active");
			} catch {
				// ignore storage access errors
			}
			// Simple reload - the server will serve the new version
			// Service worker will update on next install
			window.location.reload();
		}
	};

	const dismissUpdate = useCallback(() => {
		setHasUpdate(false);
		// Remember dismissal persistently (survives refresh) using localStorage
		if (typeof window !== "undefined") {
			localStorage.setItem("update-dismissed", serverVersion);
		}
	}, [serverVersion]);

	// Check for server version updates - this is the primary detection method
	const checkServerVersion = useCallback(async () => {
		// Don't check if user already dismissed this version (check localStorage for persistence)
		const dismissedVersion = typeof window !== "undefined" ? localStorage.getItem("update-dismissed") : null;

		try {
			const response = await fetch("/api/version", {
				cache: "no-store",
				headers: {
					"Cache-Control": "no-cache, no-store, must-revalidate",
					Pragma: "no-cache",
				},
			});

			if (response.ok) {
				const data = await response.json();
				const newServerVersion = data.version;

				if (newServerVersion) {
					setServerVersion(newServerVersion);

					// Compare with the client's build-time version
					if (newServerVersion !== CLIENT_VERSION) {
						// Only show if not dismissed
						if (dismissedVersion !== newServerVersion) {
							setHasUpdate(true);
							console.info(`[Version] Update available: ${CLIENT_VERSION} → ${newServerVersion}`);
						}
					} else {
						setHasUpdate(false);
					}
				}
			}
		} catch (error) {
			console.error("[Version] Check failed:", error);
		}
	}, []);

	// Set up periodic version checking (disabled in development)
	useEffect(() => {
		// Skip version checking in development mode
		if (IS_DEV) return;

		// Initial check after a short delay
		const initialTimeout = setTimeout(() => {
			if (!hasCheckedRef.current) {
				hasCheckedRef.current = true;
				checkServerVersion();
			}
		}, 3000);

		// Set up interval for subsequent checks
		const interval = setInterval(checkServerVersion, checkInterval);

		return () => {
			clearTimeout(initialTimeout);
			clearInterval(interval);
		};
	}, [checkInterval, checkServerVersion]);

	// Listen for service worker update messages (secondary detection, disabled in dev)
	useEffect(() => {
		if (IS_DEV) return;
		if (!("serviceWorker" in navigator)) return;

		const handleMessage = (event: MessageEvent) => {
			if (event.data?.type === "UPDATE_AVAILABLE") {
				const dismissedVersion = typeof window !== "undefined" ? localStorage.getItem("update-dismissed") : null;
				if (dismissedVersion !== event.data.version) {
					setHasUpdate(true);
					setServerVersion(event.data.version);
				}
			}
		};

		navigator.serviceWorker.addEventListener("message", handleMessage);
		return () => navigator.serviceWorker.removeEventListener("message", handleMessage);
	}, []);

	return {
		hasUpdate,
		currentVersion: CLIENT_VERSION,
		serverVersion,
		reloadToUpdate,
		dismissUpdate,
	};
}
